import { useState, useEffect } from 'react';
import { CheckOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import {
  mapPositionDTOsToItems,
  SelectPositionStep,
  type PositionItem,
} from '@/components/SelectPosition';
import ResumePreview from '@/components/ResumePreview';
import ResumeUpload from '@/components/ResumeUpload';
import { createInterview, getInterviewPositions } from '@/services/interview';
import { getResumeList } from '@/services/user';
import { allInterviewMessagesState, nextMessageIdState } from '@/store/message';
import type { ResumeDTO } from '@/types/resume';
import PointsGetter from '@/components/PointsGetter';
import { InterviewPositionCode } from '@/types/interview';

export interface StartInterviewFlowModalProps {
  open: boolean;
  onCancel: () => void;
  /** 默认选中的岗位 code */
  defaultPositionCode?: InterviewPositionCode;
}

/** 步骤指示器：① 选择岗位 ───●─── ② 确认简历（可选） */
function StepIndicator({ currentStep }: { currentStep: 1 | 2 }) {
  return (
    <div
      className="mb-6 flex items-center justify-center gap-2 sm:gap-4"
      role="tablist"
      aria-label="流程步骤"
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
            currentStep >= 1 ? 'bg-[#6366F1] text-white' : 'bg-slate-200 text-slate-500'
          }`}
          aria-current={currentStep === 1 ? 'step' : undefined}
        >
          {currentStep > 1 ? <CheckOutlined /> : '1'}
        </span>
        <span className="text-sm font-medium text-slate-700">选择岗位</span>
      </div>
      <div
        className={`h-0.5 flex-1 max-w-[80px] sm:max-w-[120px] ${
          currentStep > 1 ? 'bg-[#6366F1]' : 'bg-slate-200'
        }`}
      />
      <div className="flex items-center gap-2">
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
            currentStep >= 2 ? 'bg-[#6366F1] text-white' : 'bg-slate-200 text-slate-500'
          }`}
          aria-current={currentStep === 2 ? 'step' : undefined}
        >
          {currentStep === 2 ? '2' : '○'}
        </span>
        <span className="text-sm font-medium text-slate-700">确认简历（可选）</span>
      </div>
    </div>
  );
}

export function StartInterviewFlowModal({ open, onCancel, defaultPositionCode }: StartInterviewFlowModalProps) {
  const [flowStep, setFlowStep] = useState<1 | 2>(1);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  /** 本场面试使用的简历：来自已有列表首条或本步新上传；跳过时传 0 创建 */
  const [resumeForInterview, setResumeForInterview] = useState<ResumeDTO | null>(null);
  const [positions, setPositions] = useState<PositionItem[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [resumeListLoading, setResumeListLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pointsModalOpen, setPointsModalOpen] = useState(false);

  const setNextMessageId = useSetRecoilState(nextMessageIdState);
  const setAllMessages = useSetRecoilState(allInterviewMessagesState);
  const navigate = useNavigate();

  // Step 1: 拉取岗位列表
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFlowStep(1);
    setSelectedPositionId(null);
    setResumeForInterview(null);
    const load = async () => {
      setPositionsLoading(true);
      try {
        const list = await getInterviewPositions();
        if (!cancelled) {
          const items = mapPositionDTOsToItems(list);
          setPositions(items);
          // 如果有默认岗位 code，找到对应的 positionNumber 并选中
          if (defaultPositionCode) {
            const defaultPosition = list.find((p) => p.code === defaultPositionCode);
            if (defaultPosition) {
              setSelectedPositionId(defaultPosition.positionNumber);
            }
          }
        }
      } finally {
        if (!cancelled) setPositionsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, defaultPositionCode]);

  // Step 2: 拉取简历列表（取第一条作为当前简历）
  useEffect(() => {
    if (!open || flowStep !== 2) return;
    let cancelled = false;
    setResumeListLoading(true);
    getResumeList()
      .then((data) => {
        if (!cancelled && data.items?.length) {
          setResumeForInterview(data.items[0]);
        }
      })
      .finally(() => {
        if (!cancelled) setResumeListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, flowStep]);

  const handleNextFromStep1 = () => {
    if (selectedPositionId != null) setFlowStep(2);
  };

  const doCreateInterview = async (resumeNumber: number | undefined) => {
    if (selectedPositionId == null) return;
    try {
      setCreateLoading(true);
      const { interviewNumber, nextMessageId } = await createInterview(
        selectedPositionId,
        resumeNumber,
      );
      onCancel();
      setNextMessageId(nextMessageId);
      setAllMessages((prev) => ({
        ...prev,
        [interviewNumber]: [
          {
            role: 'assistant',
            messageId: nextMessageId,
            content: '',
            thinking: true,
            timestamp: Date.now(),
          },
        ],
      }));
      navigate(`/interview/${interviewNumber}`);
    } catch (error) {
      if ((error as Error).message.includes('积分不足')) {
        onCancel();
        setPointsModalOpen(true);
      }
      // eslint-disable-next-line no-console
      console.error('创建面试失败:', error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleStartWithResume = () => doCreateInterview(resumeForInterview?.resumeNumber);
  const handleSkipResume = () => doCreateInterview(undefined);

  const handleUploadSuccess = (uploaded: ResumeDTO) => {
    setResumeForInterview(uploaded);
    setUploadModalOpen(false);
  };

  const isStep2Ready = flowStep === 2 && selectedPositionId != null;

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={680}
        closable
        styles={{
          content: {
            background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
            borderRadius: '20px',
            padding: '24px',
          },
        }}
        className="max-w-[95vw]"
      >
        <StepIndicator currentStep={flowStep} />

        {flowStep === 1 && (
          <>
            <div className="mb-4 text-center">
              <h2 className="text-xl font-bold text-slate-800">选择面试岗位</h2>
              <p className="mt-1 text-sm text-slate-500">小灵将根据岗位特点进行针对性提问</p>
            </div>
            <SelectPositionStep
              positions={positions}
              selectedId={selectedPositionId ?? undefined}
              onSelect={setSelectedPositionId}
              loading={positionsLoading}
            />
            <div className="flex justify-end">
              <Button
                type="primary"
                size="large"
                disabled={selectedPositionId == null}
                className="h-12 rounded-xl font-medium shadow-lg"
                style={{
                  background:
                    selectedPositionId != null
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                      : undefined,
                  borderColor: 'transparent',
                }}
                onClick={handleNextFromStep1}
              >
                下一步
              </Button>
            </div>
          </>
        )}

        {flowStep === 2 && (
          <>
            <div className="mb-4 text-center">
              <h2 className="text-xl font-bold text-slate-800">确认简历（可选）</h2>
              <p className="mt-1 text-sm text-slate-500">可查看/更换简历，或跳过直接开始面试</p>
            </div>

            {resumeListLoading ? (
              <div className="animate-pulse rounded-2xl border border-slate-200 bg-white/60 p-6">
                <div className="mb-3 h-5 w-32 rounded bg-slate-200" />
                <div className="h-4 w-48 rounded bg-slate-200" />
              </div>
            ) : resumeForInterview ? (
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                      <FileTextOutlined />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800">
                        {resumeForInterview.fileName}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        上传于 {resumeForInterview.createdAt?.slice(0, 10)}
                        {resumeForInterview.parseStatus === 'completed' && ' · 已解析 ✓'}
                        {resumeForInterview.parseStatus === 'parsing' && ' · 解析中...'}
                      </div>
                    </div>
                  </div>
                  <Button type="link" size="small" onClick={() => setUploadModalOpen(true)}>
                    更换
                  </Button>
                </div>
                {resumeForInterview.resumeMetadata && (
                  <div className="mt-4">
                    <ResumePreview
                      metadata={resumeForInterview.resumeMetadata}
                      maxHeight="min(320px, 40vh)"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
                <p className="text-sm text-slate-500">暂无简历，可上传或跳过直接开始面试</p>
                <Button
                  type="primary"
                  ghost
                  className="mt-3"
                  onClick={() => setUploadModalOpen(true)}
                >
                  上传简历
                </Button>
              </div>
            )}

            {!resumeListLoading && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <Button
                  type="primary"
                  size="large"
                  className="h-12 min-w-[200px] rounded-xl font-medium shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    borderColor: 'transparent',
                  }}
                  onClick={handleStartWithResume}
                  loading={createLoading}
                  disabled={!isStep2Ready}
                >
                  开始面试
                </Button>
                <Button
                  type="text"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={handleSkipResume}
                  disabled={createLoading || !isStep2Ready}
                >
                  跳过，直接开始面试
                </Button>
              </div>
            )}
          </>
        )}
      </Modal>

      <ResumeUpload
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      <Modal
        open={pointsModalOpen}
        onCancel={() => setPointsModalOpen(false)}
        footer={null}
        width={750}
        closable
      >
        <PointsGetter
          onPaySuccess={() => {
            setPointsModalOpen(false);
            void (
              selectedPositionId != null && doCreateInterview(resumeForInterview?.resumeNumber)
            );
          }}
        />
      </Modal>
    </>
  );
}
